import React from "react";
import { useAuth } from "@/hooks/useAuth";
import { createSeason } from "@/services/seasonService";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import SeasonForm, { SeasonFormData } from "./SeasonForm";

interface AddSeasonDialogProps {
  onSeasonAdded: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const AddSeasonDialog: React.FC<AddSeasonDialogProps> = ({ 
  onSeasonAdded, 
  open: controlledOpen, 
  onOpenChange 
}) => {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;
  
  const { user } = useAuth();
  const organizationId = user?.user_metadata?.organization_id || "00000000-0000-0000-0000-000000000001";

  const handleSubmit = async (data: SeasonFormData) => {
    try {
      // Format the dates correctly
      const formattedData = {
        ...data,
        name: data.name, // Ensure name is required
        organization_id: organizationId,
        start_date: data.start_date,
        end_date: data.end_date,
        registration_start: data.registration_start,
        registration_end: data.registration_end,
        registration_fee_enabled: !!data.registration_fee_enabled,
        tuition_prepay_enabled: !!data.tuition_prepay_enabled,
        is_active: !!data.is_active
      };

      const result = await createSeason(formattedData);
      
      if (result) {
        setOpen(false);
        onSeasonAdded();
      }
    } catch (error) {
      console.error("Error adding season:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          className="bg-rosterly-purple hover:bg-rosterly-purple/90"
          data-add-season-trigger
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Season
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Season</DialogTitle>
          <DialogDescription>
            Create a new season for classes and programs.
          </DialogDescription>
        </DialogHeader>
        <SeasonForm 
          onSubmit={handleSubmit} 
          organizationId={organizationId} 
        />
      </DialogContent>
    </Dialog>
  );
};

export default AddSeasonDialog;
